import { useCallback, useEffect, useState } from "react";
import useJouneyReq from "../../../../components/hook/bot-req";
import useRequest from "../../../../components/hook/use-req";
import { useTenantId } from "../../../../components/hook/use-tenant-id";

// Real bot-builder backend endpoints (journeybuilder.connect.dalmlabs.com),
// per bot-builder-api.postman_collection.json ("Status: deployed and live on
// prod"). Palette, journeys, fetch-profile, ai-generate and the test-run gate
// are open; whatsapp-templates, sms-campaigns and the SMS leg of
// test-run/send need the logged-in user's own portal JWT + X-TENANT.

// Confirmed live across several of these routes: a non-2xx body isn't always
// {"detail": "<string>"} — audience/preview uses {"detail": {"why": "<a
// sentence written to be shown as-is>", ...}}, and flows/publish + flows/run
// use {"detail": {"error": "...", "blockers": [...]}}. Every hook below used
// to check only the plain-string shape and fall back to a generic message
// otherwise, which silently swallowed the real reason on compile, publish and
// save (flagged directly by backend — useAudiencePreview was the only one
// already reading `.why`). Checked in this order so a real message always
// wins over the generic fallback.
function extractErrorDetail(json: any, fallback: string): string {
  if (typeof json?.detail === "string") return json.detail;
  if (typeof json?.detail?.why === "string") return json.detail.why;
  if (typeof json?.detail?.error === "string") return json.detail.error;
  return fallback;
}

export interface FetchProfileResult {
  source: "golden" | "demo" | string;
  unique_id: string | null;
  profile: Record<string, any>;
  // Backend, 26 Oct: whether the phone/unique_id resolved to a real warehouse
  // profile, and if not, why (e.g. "no tenant_id on this request …") — a
  // false here means `profile` is the built-in demo (Ramesh Kumar).
  matched?: boolean;
  reason?: string | null;
}

// No auth required. The backend genuinely requires phone OR unique_id
// despite both being marked optional in the field table — callers must
// supply one; there's no context in which calling this with neither succeeds.
// tenant_id is sent too: confirmed live, without it the lookup never reads
// any tenant's warehouse and always serves the demo profile.
export function useFetchProfile() {
  const tenantId = useTenantId();
  const { loading, makeRequest } = useJouneyReq("/v1/bot/fetch-profile", "POST");

  const fetchProfile = useCallback(
    async (params: { phone?: string; unique_id?: string | null; attributes?: string[] }): Promise<FetchProfileResult | null> => {
      const [json, status] = await makeRequest({ ...params, ...(tenantId ? { tenant_id: tenantId } : {}) });
      if (status >= 200 && status < 300 && json?.profile) return json;
      return null;
    },
    [makeRequest, tenantId]
  );

  return { loading, fetchProfile };
}

export type JourneyChannel = "whatsapp" | "sms" | "email";

export interface AiGeneratedChannel {
  body?: string;
  subject?: string;
  html_body?: string;
  cta_text?: string;
  variables?: string[];
}

export interface AiGenerateResult {
  provider: string;
  model: string;
  channels: Partial<Record<JourneyChannel, AiGeneratedChannel>>;
}

// No auth required. 503 = the provider key isn't configured server-side —
// surface that, never invent fallback copy. Overloaded models no longer
// 503 — the API falls back across Gemini models automatically.
export function useAiGenerate() {
  const { loading, makeRequest } = useJouneyReq("/v1/bot/ai-generate", "POST");

  const generate = useCallback(
    async (body: {
      instruction: string;
      profile: Record<string, any>;
      channels: JourneyChannel[];
      tone?: string;
      length?: string;
      options?: any[];
    }): Promise<{ result: AiGenerateResult | null; unavailable: boolean }> => {
      const [json, status] = await makeRequest(body);
      if (status >= 200 && status < 300 && json?.channels) return { result: json, unavailable: false };
      return { result: null, unavailable: status === 503 };
    },
    [makeRequest]
  );

  return { loading, generate };
}

export interface TestRunDeliveryResult {
  channel: JourneyChannel;
  enqueued: boolean;
  task?: string;
  task_id?: string;
  via?: string;
  status?: number;
  error?: string;
}

export interface TestRunSendResult {
  profile: Record<string, any>;
  generated: AiGenerateResult;
  delivery: TestRunDeliveryResult[];
}

// The one endpoint that can actually dispatch a real WhatsApp/SMS/email —
// `deliver: true` requires the caller to have gotten explicit opt-in first
// (see test-run-modal.tsx's checkbox); this hook itself doesn't default it.
// Requires the caller's own session: Authorization + X-TENANT, both resolved
// from the same source use-tenant-id.ts already uses elsewhere in the app.
export function useTestRunSend() {
  const tenantId = useTenantId();
  const token = localStorage.getItem("authToken");
  const { loading, makeRequest } = useJouneyReq("/v1/bot/test-run/send", "POST", {
    Authorization: `Bearer ${token}`,
    "X-TENANT": tenantId,
  });

  const sendTestRun = useCallback(
    async (body: {
      phone: string;
      email?: string;
      channels: JourneyChannel[];
      instruction: string;
      tone?: string;
      length?: string;
      whatsapp_template?: string;
      sms_campaign_id?: string;
      // Real field confirmed live on POST /v1/bot/test-run/send's own schema
      // (bot_builder__routes__TestSendRequest): "What to send, per channel,
      // when the operator wrote it or picked a template — e.g. {"email":
      // {"subject", "html_body"}, "sms": {"body"}}. Supplied content is sent
      // AS IS; AI Generate only runs for channels with no content here" — the
      // real mechanism for "send exactly this", superseding the old approach
      // of folding a template's body into `instruction` as a strict directive
      // and hoping the model complied with it word for word.
      content?: Record<string, { body?: string; subject?: string; html_body?: string }>;
      deliver: boolean;
    }): Promise<{ result: TestRunSendResult | null; error: string | null }> => {
      const [json, status] = await makeRequest({ ...body, tenant_id: tenantId, jwt: token });
      if (status >= 200 && status < 300 && json?.delivery) return { result: json, error: null };
      return { result: null, error: typeof json?.detail === "string" ? json.detail : "Couldn't reach the test-run service." };
    },
    [makeRequest, tenantId, token]
  );

  return { loading, sendTestRun, tenantReady: !!tenantId };
}

export interface TestRunGateResult {
  test_run_passed: boolean;
  blocking_reasons: string[];
}

// The publish *gate* — POST /v1/bot/test-run (no /send, nothing is ever
// dispatched). No auth required. Body is {flow, trigger?, channel?,
// simulate?} — flow-model.ts's serializeFlow builds `flow` from the current
// trigger + chain.
export function useTestRunGate() {
  const { loading, makeRequest } = useJouneyReq("/v1/bot/test-run", "POST");

  const runGate = useCallback(
    async (flow: { startNodeId: string; nodes: any[]; edges: any[] }, trigger?: Record<string, any>, channel?: JourneyChannel): Promise<TestRunGateResult> => {
      const [json, status] = await makeRequest({ flow, trigger: trigger || {}, channel: channel || "whatsapp", simulate: true });
      if (status >= 200 && status < 300 && typeof json?.test_run_passed === "boolean") {
        return { test_run_passed: json.test_run_passed, blocking_reasons: json.blocking_reasons || [] };
      }
      return {
        test_run_passed: false,
        blocking_reasons: [extractErrorDetail(json, "Couldn't reach the test-run gate.")],
      };
    },
    [makeRequest]
  );

  return { loading, runGate };
}

// POST {hostUrl}/journeys/{journey_id}/publish — a different path than every
// other hook here (no /v1/bot prefix), but the same journeybuilder host
// (useJouneyReq's base already matches hostUrl), so it still goes through
// useJouneyReq with the full path spelled out. Tenant comes from the auth
// context server-side — only the bearer token is needed.
export function usePublishJourney() {
  const token = localStorage.getItem("authToken");
  const { loading, makeRequest } = useJouneyReq("/journeys", "POST", { Authorization: `Bearer ${token}` });

  const publish = useCallback(
    async (journeyId: string | number, version?: number): Promise<{ ok: boolean; error: string | null }> => {
      const [json, status] = await makeRequest(version ? { version } : undefined, undefined, `${journeyId}/publish`);
      if (status >= 200 && status < 300) return { ok: true, error: null };
      return { ok: false, error: extractErrorDetail(json, "Couldn't publish this bot.") };
    },
    [makeRequest]
  );

  return { loading, publish };
}

export function useUnpublishJourney() {
  const token = localStorage.getItem("authToken");
  const { loading, makeRequest } = useJouneyReq("/journeys", "POST", { Authorization: `Bearer ${token}` });

  const unpublish = useCallback(
    async (journeyId: string | number): Promise<{ ok: boolean; error: string | null }> => {
      const [json, status] = await makeRequest(undefined, undefined, `${journeyId}/unpublish`);
      if (status >= 200 && status < 300) return { ok: true, error: null };
      return { ok: false, error: extractErrorDetail(json, "Couldn't unpublish this bot.") };
    },
    [makeRequest]
  );

  return { loading, unpublish };
}

export interface CreateJourneyResult {
  id: number;
  name: string;
  tenant: string;
  status: string;
  current_draft_version: number;
}

// POST {hostUrl}/journeys — confirmed live against the real OpenAPI schema.
// Creates the journey record and returns the real integer id
// POST /journeys/{id}/publish needs — there was previously no way to get one
// from the canvas at all, which is why Publish was permanently disabled.
// `definition` is the engine-ready step list from POST /v1/bot/flows/compile
// (journey_definition), not the canvas's own {startNodeId,nodes,edges} graph.
// `tenant`/`organization_id` added per backend (Joseph Dalm), who confirmed
// this exact body live on prod with his own account's token — this is also
// the draft-creation step Test Run now reuses (create, skip /publish, then
// POST /v1/bot/flows/run against the draft id directly). No separate
// organization-id source exists anywhere in this app yet, so it's resolved
// from the same tenant id useTenantId() already gives every other hook here;
// flag to backend if that's ever actually a different value.
export function useCreateJourney() {
  const token = localStorage.getItem("authToken");
  const tenantId = useTenantId();
  const { loading, makeRequest } = useJouneyReq("/journeys", "POST", { Authorization: `Bearer ${token}` });

  const create = useCallback(
    async (body: { name: string; definition: Record<string, any>; channel?: string; account?: string }): Promise<{ journey: CreateJourneyResult | null; error: string | null }> => {
      const [json, status] = await makeRequest({ ...body, tenant: tenantId, organization_id: tenantId });
      if (status >= 200 && status < 300) return { journey: json, error: null };
      return { journey: null, error: extractErrorDetail(json, "Couldn't save this journey.") };
    },
    [makeRequest, tenantId]
  );

  return { loading, create };
}

export interface CreateVersionResult {
  id: number;
  journey_id: number;
  version: number;
  status: string;
}

// POST {hostUrl}/journeys/{id}/versions — saves a new draft version onto an
// *already-created* journey (re-publishing the same bot after further edits
// should go through this, not create a second journey record every time).
export function useCreateJourneyVersion() {
  const token = localStorage.getItem("authToken");
  const { loading, makeRequest } = useJouneyReq("/journeys", "POST", { Authorization: `Bearer ${token}` });

  const createVersion = useCallback(
    async (journeyId: number, definition: Record<string, any>): Promise<{ version: CreateVersionResult | null; error: string | null }> => {
      const [json, status] = await makeRequest({ definition }, undefined, `${journeyId}/versions`);
      if (status >= 200 && status < 300) return { version: json, error: null };
      return { version: null, error: extractErrorDetail(json, "Couldn't save a new version.") };
    },
    [makeRequest]
  );

  return { loading, createVersion };
}

export interface FlowCompileResult {
  journey_definition: Record<string, any>;
  step_count: number;
  blockers: string[];
}

// POST /v1/bot/flows/compile — confirmed live, no auth. Converts the canvas
// graph into the step list the engine actually runs, and reports any
// `blockers` that would make a published run stop silently (e.g. a step type
// the engine doesn't implement yet, like Fraud Alert's Send Push right now).
// This is the new, engine-accurate check — the older /v1/bot/test-run
// simulate gate (useTestRunGate above) doesn't know about engine-level
// limitations like a missing provider.
export function useCompileFlow() {
  const { loading, makeRequest } = useJouneyReq("/v1/bot/flows/compile", "POST");

  const compile = useCallback(
    async (flow: { startNodeId: string; nodes: any[]; edges: any[] }, contactId: string): Promise<{ result: FlowCompileResult | null; error: string | null }> => {
      const [json, status] = await makeRequest({ flow, contact_id: contactId });
      if (status >= 200 && status < 300) return { result: json, error: null };
      return { result: null, error: extractErrorDetail(json, "Couldn't compile this flow.") };
    },
    [makeRequest]
  );

  return { loading, compile };
}

// POST /v1/bot/flows/run {flow, journey_id, contact_id, tenant_id, data,
// dry_run} — confirmed by backend (Joseph Dalm) against prod with his own
// account's token: the real path is POST /journeys (useCreateJourney above)
// to get a journey_id, *skip* /journeys/{id}/publish for a test run, then
// call this directly against the draft. `data` (the Test Run modal's own
// field values, e.g. kyc_completion_status) drives every real Condition node
// exactly like a live event would — this is what makes the run show up in
// the tenant's Executions tab and in Temporal. `dry_run: true` still runs the
// whole journey for real (conditions, branches, AI, the execution row) and
// only skips the actual sends — this is what "Actually send this test"
// unchecked now maps to (builder-page.tsx passes dryRun = !deliver), so an
// unticked checkbox still produces a real trace instead of not calling this
// endpoint at all. Replaces the earlier (incorrect) attempt at this fix,
// which called a since-corrected /v1/bot/flows/publish endpoint that isn't
// the right one — and, before that, /v1/bot/test-run/send, which only ever
// does fetch-profile -> ai-generate -> deliver with no Condition/branch logic
// at all. Response (confirmed by backend, 27 Oct): {status: "started",
// workflow_id, steps, trigger} — the run itself finishes asynchronously, so
// the real outcome (outcome_reason, emails_dispatched, send_failures) is read
// afterwards from GET /v1/executions/{workflow_id}'s submitted_data — see
// builder-page.tsx's describeRunOutcome. `dry_run` is live on prod.
export interface FlowRunStarted {
  status: string;
  workflow_id?: string;
  steps?: number;
  trigger?: string;
}

export function useFlowRun() {
  const tenantId = useTenantId();
  const { loading, makeRequest } = useJouneyReq("/v1/bot/flows/run", "POST");

  const runFlow = useCallback(
    async (
      flow: { startNodeId: string; nodes: any[]; edges: any[] },
      journeyId: string | number,
      contactId: string,
      data: Record<string, any>,
      dryRun: boolean
    ): Promise<{ result: FlowRunStarted | null; error: string | null }> => {
      // Confirmed live: `journey_id` 422s ("Input should be a valid string")
      // when sent as the plain number POST /journeys itself returns — needs
      // stringifying here regardless of what the caller passed in.
      // `fast_forward: true` (backend, 23 Oct) collapses every Wait so a Test
      // Run plays the whole journey in seconds — conditions, branches, AI and
      // sends still run normally. Without it a test honoured waits like a live
      // run: the welcome bot parked at its 1-day wait and abandonment sent
      // nothing (first message is 24h in). Always on: this hook only backs the
      // Test Run modal — published journeys never go through /flows/run.
      const [json, status] = await makeRequest({ flow, journey_id: String(journeyId), contact_id: contactId, tenant_id: tenantId, data, dry_run: dryRun, fast_forward: true });
      if (status >= 200 && status < 300) return { result: json, error: null };
      return { result: null, error: extractErrorDetail(json, "Couldn't run this test.") };
    },
    [makeRequest, tenantId]
  );

  return { loading, runFlow };
}

export interface WhatsAppTemplateOption {
  id: string;
  template_name: string;
  content: string;
  category?: string;
  language?: string;
  wa_status: string;
  buttons?: any;
  header_type?: string | null;
  header_content?: string | null;
  variables?: { name: string; index: number; example: string }[] | null;
}

// Not the bot-specific /v1/bot/whatsapp-templates (that one wasn't returning
// results for this tenant) — the SAME /templates/ listing the real Campaign
// "Messaging" step and the Templates page already use on the core connect
// API (components/marketing/campaign/messaging.tsx), filtered to WhatsApp
// and to APPROVED (the only actually sendable) templates, same as that flow.
export function useWhatsAppTemplates(enabled: boolean) {
  const token = localStorage.getItem("authToken");
  const { loading, makeRequest } = useRequest("/templates/", "GET", { Authorization: `Bearer ${token}` });
  const [templates, setTemplates] = useState<WhatsAppTemplateOption[]>([]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      const [response] = await makeRequest(undefined, { page: 1, limit: 100, channel: "WhatsApp" });
      if (cancelled) return;
      const raw = response?.data ?? (Array.isArray(response) ? response : []);
      setTemplates(raw.filter((t: any) => t?.wa_status === "APPROVED"));
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { loading, templates };
}

export interface EmailTemplateOption {
  id: string;
  template_name: string;
  subject?: string;
  html?: string;
  plain_text?: string;
  // Real field, confirmed via components/marketing/campaign/email-content-creation.tsx's
  // own TemplateCard — used there for the Templates gallery's thumbnail.
  snapshot_url?: string | null;
}

// Same /templates/ endpoint as useWhatsAppTemplates above, channel=Email —
// matches the exact mapping components/marketing/campaign/email-content-creation.tsx
// already uses for its own template picker (t.html_content || t.content for
// the HTML body, t.content as the plain-text fallback).
export function useEmailTemplates(enabled: boolean) {
  const token = localStorage.getItem("authToken");
  const { loading, makeRequest } = useRequest("/templates/", "GET", { Authorization: `Bearer ${token}` });
  const [templates, setTemplates] = useState<EmailTemplateOption[]>([]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      const [response] = await makeRequest(undefined, { page: 1, limit: 100, channel: "Email" });
      if (cancelled) return;
      const raw = response?.data ?? (Array.isArray(response) ? response : []);
      setTemplates(
        raw.map((t: any) => ({
          id: t.id,
          template_name: t.template_name || "Untitled Template",
          subject: t.subject,
          html: t.html_content || t.content,
          plain_text: t.content || "",
          snapshot_url: t.snapshot_url || null,
        }))
      );
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { loading, templates };
}

export interface SmsCampaignOption {
  id: string;
  name: string;
  status: string;
  channel: string;
}

// GET /v1/bot/sms-campaigns — one page only (~10, portal-paginated), so
// `count` here is the page size, not the tenant's total. Same auth as
// whatsapp-templates. The selected `id` (a UUID) is what test-run/send wants
// as `sms_campaign_id`.
export function useSmsCampaigns(enabled: boolean) {
  const tenantId = useTenantId();
  const token = localStorage.getItem("authToken");
  const { loading, makeRequest } = useJouneyReq("/v1/bot/sms-campaigns", "GET", {
    Authorization: `Bearer ${token}`,
    "X-TENANT": tenantId,
  });
  const [campaigns, setCampaigns] = useState<SmsCampaignOption[]>([]);

  useEffect(() => {
    if (!enabled || !tenantId) return;
    let cancelled = false;
    (async () => {
      const [json, status] = await makeRequest();
      if (cancelled) return;
      if (status >= 200 && status < 300 && Array.isArray(json?.campaigns)) setCampaigns(json.campaigns);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, tenantId]);

  return { loading, campaigns };
}

export interface ProfileAttribute {
  id: string;
  label: string;
  default: boolean;
  // Confirmed live (GET /v1/bot/profile-attributes?design=abbas) — not every
  // listed attribute is an actual warehouse column yet (9 of 13 in that
  // design are `available: false`, e.g. churn_risk_score, dormancy_risk_score,
  // monthly_salary_3m). Ticking one that isn't available builds a fetch
  // condition that silently never matches — this must disable the checkbox,
  // not just annotate it, same gating `queryable` already does for trait
  // attributes elsewhere in this file.
  available?: boolean;
  recommended?: boolean;
}

export interface ProfileAttributeGroup {
  group: string;
  attributes: ProfileAttribute[];
}

export interface ProfileAttributesResult {
  journey: string | null;
  groups: ProfileAttributeGroup[];
  defaults: string[];
}

// GET /v1/bot/profile-attributes?journey= — confirmed live, no auth. The
// grouped checkboxes on the Fetch Profile card. Groups differ per journey
// (Customer info + KYC status for new_account_welcome, + Loan information
// for loan_abandonment_recovery, + Transaction information for
// fraud_anomaly_alert) — read them rather than assuming a fixed shape.
// `defaults` is the same attributes flattened — POST it straight back as the
// node's own `fetch` list, and as `attributes` to fetch-profile.
export function useProfileAttributes(enabled: boolean, journeyId?: string) {
  const { loading, makeRequest } = useJouneyReq("/v1/bot/profile-attributes", "GET");
  const [data, setData] = useState<ProfileAttributesResult | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      const [json, status] = await makeRequest(undefined, journeyId ? { journey: journeyId } : undefined);
      if (cancelled) return;
      if (status >= 200 && status < 300 && Array.isArray(json?.groups)) setData(json);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, journeyId]);

  return { data, loading };
}

export interface VariableItem {
  id: string;
  label: string;
  example?: string;
}

export interface VariableGroup {
  group: string;
  items: VariableItem[];
}

let variablesCache: { loaded: boolean; groups: VariableGroup[] } = { loaded: false, groups: [] };

// GET /v1/bot/variables — confirmed live, no auth. The real insertable merge
// fields ("Add variable" on every Send/AI Generate card) — grouped
// (Customer/Bank/Links/Loan/Transaction/...), each with its own real `id`
// (the exact token to wrap in {{...}}), label and example value. Cached at
// module level (same static list everywhere in the builder, not scoped per
// node/journey) rather than refetched on every card that renders a picker.
export function useVariables() {
  const { makeRequest } = useJouneyReq("/v1/bot/variables", "GET");
  const [groups, setGroups] = useState<VariableGroup[]>(variablesCache.groups);
  const [loading, setLoading] = useState(!variablesCache.loaded);

  useEffect(() => {
    if (variablesCache.loaded) return;
    let cancelled = false;
    (async () => {
      const [json, status] = await makeRequest();
      if (cancelled) return;
      if (status >= 200 && status < 300 && Array.isArray(json?.groups)) {
        variablesCache = { loaded: true, groups: json.groups };
        setGroups(json.groups);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { groups, loading };
}

export interface TriggerOptionLayer {
  id: string;
  label: string;
  required: boolean;
  description?: string;
  from?: string;
}

export interface TriggerOptionKind {
  id: "event" | "trait" | string;
  label: string;
  description: string;
  example?: string;
  config: Record<string, string>;
  // Confirmed live 20 Oct: event is "optional" (narrows/excludes only — the
  // event already named the customer, so this filter can't select anyone new)
  // and trait is `true` (required — nothing else says who). Was `false` on
  // event in the original brief; Anshul changed that on the 18th. Always read
  // this flag rather than hardcoding a kind's audience-filter visibility.
  takes_audience_filter?: boolean | "optional";
  takes_schedule?: boolean;
  // Confirmed `false` on event, 20 Oct — "frequency is nothing but a retry
  // kind of a thing, remove that part for now." No frequency control exists
  // in this panel for any kind, so there's nothing to remove; kept here so a
  // future `true` is visible rather than silently ignored.
  takes_frequency?: boolean;
  layers?: TriggerOptionLayer[];
}

export interface TriggerOperator {
  id: string;
  label: string;
  // How many values this operator needs — 2 for "between" (value + value_to),
  // 1 (the default, when absent) for everything else.
  takes?: number;
  hint?: string;
}

export interface TraitAttribute {
  id: string;
  label: string;
  type: "number" | "band" | "string" | "date" | string;
  range?: [number, number];
  bands?: string[];
  example_bands?: [number, number][];
  // False means this attribute is named as available on ConvoUnify but not
  // yet verified on a live profile — surface that rather than promising it
  // works the same as a confirmed one.
  confirmed: boolean;
  hint?: string;
  operators: TriggerOperator[];
  // Confirmed live 20 Oct — the real signal for whether the sweep resolver
  // can filter on this at all (it 400s if asked to on a non-queryable
  // attribute). `confirmed` is a separate, older "verified on a live
  // profile" flag; keep both, they answer different questions — this one
  // gates enablement, `confirmed` only changes the label's wording.
  queryable?: boolean;
  unavailable_reason?: string;
}

export interface TriggerSchedule {
  id: string;
  label: string;
  fields: string[];
  default: Record<string, any>;
  hint?: string;
}

export interface TriggerReEntry {
  id: string;
  label: string;
  fields?: string[];
  default?: Record<string, any>;
  hint?: string;
}

export interface TriggerEventOption {
  id: string;
  label: string;
  description?: string;
  status: string;
  node_type?: string;
  config?: Record<string, any>;
  source?: Record<string, any>;
  workflow_settings?: Record<string, any>;
  event_example?: Record<string, any>;
}

export interface TriggerOptionsResult {
  kinds: TriggerOptionKind[];
  trait_attributes: TraitAttribute[];
  schedules: TriggerSchedule[];
  re_entry: TriggerReEntry[];
  events: TriggerEventOption[];
}

let triggerOptionsCache: { loaded: boolean; data: TriggerOptionsResult | null } = { loaded: false, data: null };

// GET /v1/bot/trigger-options — confirmed live, no auth (not listed in the
// host's own /openapi.json, confirmed by hitting it directly — verify with a
// live curl before trusting that doc for this one). Replaces the old generic
// JSON URL/upload trigger panel: the real event-vs-trait choice, the full
// event catalog with each one's own example payload, every trait attribute's
// type/operators/confirmed flag, and the schedule/re-entry/debounce shapes a
// trait trigger needs. Static per tenant — cached at module level like
// useVariables above.
export function useTriggerOptions() {
  const { makeRequest } = useJouneyReq("/v1/bot/trigger-options", "GET");
  const [data, setData] = useState<TriggerOptionsResult | null>(triggerOptionsCache.data);
  const [loading, setLoading] = useState(!triggerOptionsCache.loaded);

  useEffect(() => {
    if (triggerOptionsCache.loaded) return;
    let cancelled = false;
    (async () => {
      const [json, status] = await makeRequest();
      if (cancelled) return;
      if (status >= 200 && status < 300 && Array.isArray(json?.kinds)) {
        triggerOptionsCache = { loaded: true, data: json };
        setData(json);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { data, loading };
}

export interface TriggerEventFilterableProperty {
  field: string;
  type: "number" | "string" | string;
}

export interface TriggerEventDef {
  id: string;
  label: string;
  description?: string;
  attributes?: string[];
  filterable_properties: TriggerEventFilterableProperty[];
  source?: string;
  // Only present on a "derived" event — computed from the bank's data rather
  // than existing the moment the bank is connected. Shown as a tooltip/helper
  // line, not hidden: "it is the honest answer to 'where does this come
  // from?'".
  derivation?: string;
  example_payload?: Record<string, any>;
}

export interface TriggerEventGroup {
  id: "raw" | "derived" | string;
  label: string;
  description?: string;
  events: TriggerEventDef[];
}

export interface TriggerEventsResult {
  groups: TriggerEventGroup[];
  count: number;
  // Older event names that still resolve because journeys were published
  // against them (e.g. "salary_credit_detected" -> "salary_credited") —
  // prefer the canonical name (the one actually listed in `groups`) for
  // anything new; this is just for recognizing an already-saved trigger.
  aliases?: Record<string, string>;
  note?: string;
}

let triggerEventsCache: { loaded: boolean; data: TriggerEventsResult | null } = { loaded: false, data: null };

// GET /v1/bot/trigger-events — confirmed live, no auth. Replaces
// trigger-options' own `events` field (the old generic catalogue) for the
// event-kind dropdown: the real banking event list, grouped "raw" (exists the
// moment the bank is connected) vs "derived" (computed, e.g. salary_credited)
// — keep the two groups visually separate, the distinction is deliberate.
// Static per tenant like trigger-options above, so cached the same way.
export function useTriggerEvents() {
  const { makeRequest } = useJouneyReq("/v1/bot/trigger-events", "GET");
  const [data, setData] = useState<TriggerEventsResult | null>(triggerEventsCache.data);
  const [loading, setLoading] = useState(!triggerEventsCache.loaded);

  useEffect(() => {
    if (triggerEventsCache.loaded) return;
    let cancelled = false;
    (async () => {
      const [json, status] = await makeRequest();
      if (cancelled) return;
      if (status >= 200 && status < 300 && Array.isArray(json?.groups)) {
        triggerEventsCache = { loaded: true, data: json };
        setData(json);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { data, loading };
}

export interface Assigner {
  id: string;
  label: string;
  kind: string;
}

export interface AssignersResult {
  assigners: Assigner[];
  count: number;
  default_message: string;
  variables: string[];
}

// GET /v1/bot/assigners — confirmed live, no auth. Who a case can be handed
// to on the Assign People card, plus the follow-up `default_message` the
// design pre-fills (with its own `{{...}}` variables).
export function useAssigners(enabled: boolean) {
  const { loading, makeRequest } = useJouneyReq("/v1/bot/assigners", "GET");
  const [data, setData] = useState<AssignersResult | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      const [json, status] = await makeRequest();
      if (cancelled) return;
      if (status >= 200 && status < 300 && Array.isArray(json?.assigners)) setData(json);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { data, loading };
}

export interface JourneyFlowNode {
  id: string;
  type: string;
  label: string;
  position?: { x: number; y: number };
  config: Record<string, any>;
}

export interface JourneyFlowEdge {
  id: string;
  source: string;
  target: string;
  condition: "always" | "true" | "false" | "postback" | string;
  // Only present on a `condition: "postback"` edge — which of an AI Decide
  // node's named options this path is for (confirmed live against GET
  // /v1/bot/journeys/churn_risk_escalation, e.g. "self_serve"/"high_value").
  value?: string;
}

export interface JourneyFlowResult {
  id: string;
  name: string;
  description: string;
  category: string;
  channels: string[];
  trigger_event: string;
  flow: {
    startNodeId: string;
    nodes: JourneyFlowNode[];
    edges: JourneyFlowEdge[];
  };
}

// GET /v1/bot/journeys/{id} — confirmed live, no auth. The exact prebuilt
// canvas (nodes/edges) for a ready-made journey, e.g. new_account_welcome's
// two KYC checks or loan_abandonment_recovery's 24h/48h/24h reminder cadence
// — converted into this builder's own chain model by journey-flow-import.ts.
export function useJourneyFlow(journeyId?: string) {
  const { makeRequest } = useJouneyReq("/v1/bot/journeys", "GET");
  const [data, setData] = useState<JourneyFlowResult | null>(null);
  // Own loading flag (not useJouneyReq's) initialized synchronously from
  // `journeyId` — a caller that gates seeding the canvas on "not loading"
  // (builder-page.tsx) needs that to read true from this very first render
  // whenever a journey id is present, with no window where it reads false
  // before the fetch has actually started.
  const [loading, setLoading] = useState(!!journeyId);

  useEffect(() => {
    if (!journeyId) {
      setData(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const [json, status] = await makeRequest(undefined, undefined, journeyId);
      if (cancelled) return;
      if (status >= 200 && status < 300 && json?.flow?.nodes) setData(json);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyId]);

  return { data, loading };
}

export interface SavedJourneyResult {
  id: number;
  tenant: string;
  name: string;
  status: string;
  active_version: number | null;
  channel: string | null;
  account: string | null;
  versions: { id: number; version: number; status: string; created_at: string }[];
  trigger: string | null;
  // Generic object server-side (confirmed live: JourneyDetailResponse's
  // `definition` field has no fixed schema) — whatever POST /journeys was
  // last given back verbatim. This builder also writes a `_canvas` key onto
  // it on publish (see builder-page.tsx's handlePublish) holding the exact
  // {startNodeId, nodes, edges} graph, so re-opening a bot this builder
  // created can rebuild its real canvas instead of best-effort reconstructing
  // one from the flat execution step list — see chainFromSavedDefinition.
  definition: Record<string, any> | null;
}

// GET /journeys/{id} — a real, already-created bot from this tenant's own
// list (distinct from GET /v1/bot/journeys/{id} above, which is the public
// prebuilt-template catalog). Used when opening an existing bot from "All
// Bots" for further editing, not when starting from a template.
export function useSavedJourney(id?: string | number) {
  const token = localStorage.getItem("authToken");
  const { makeRequest } = useJouneyReq("/journeys", "GET", { Authorization: `Bearer ${token}` });
  const [data, setData] = useState<SavedJourneyResult | null>(null);
  const [loading, setLoading] = useState(!!id);

  useEffect(() => {
    if (!id) {
      setData(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const [json, status] = await makeRequest(undefined, undefined, String(id));
      if (cancelled) return;
      if (status >= 200 && status < 300 && json?.id) setData(json);
      else setData(null);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return { data, loading };
}

export interface ConditionAttribute {
  id: string;
  label: string;
  values?: string[];
  type?: string;
  compare_to?: string;
  default_threshold?: { amount: number; unit: string };
  units?: string[];
}

export interface ConditionOperator {
  id: string;
  label: string;
  applies_to?: string;
}

export interface ConditionOptionsResult {
  attributes: ConditionAttribute[];
  operators: ConditionOperator[];
  end_workflow_allowed: boolean;
}

// GET /v1/bot/condition-options — confirmed live, no auth. Backs the
// Condition card's "CHECK If" attribute dropdown and the TRUE/FALSE
// operator+value selects — each attribute carries its own allowed `values`.
export function useConditionOptions(enabled: boolean) {
  const { loading, makeRequest } = useJouneyReq("/v1/bot/condition-options", "GET");
  const [data, setData] = useState<ConditionOptionsResult | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      const [json, status] = await makeRequest();
      if (cancelled) return;
      if (status >= 200 && status < 300 && Array.isArray(json?.attributes)) setData(json);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { data, loading };
}

export interface SmsTemplateOption {
  id: string;
  name: string;
  body: string;
  variables: string[];
  length: number;
  segments: number;
}

export interface SmsTemplatesResult {
  source: "portal" | "designs" | string;
  templates: SmsTemplateOption[];
  count: number;
  hint?: string;
}

// GET /v1/bot/sms-templates — confirmed live, no auth. Backs both the Send
// SMS dropdown and the phone preview (the template carries its own body,
// variables, length and segments — not just a name like sms-campaigns does).
export function useSmsTemplates(enabled: boolean) {
  const { loading, makeRequest } = useJouneyReq("/v1/bot/sms-templates", "GET");
  const [data, setData] = useState<SmsTemplatesResult | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      const [json, status] = await makeRequest();
      if (cancelled) return;
      if (status >= 200 && status < 300 && Array.isArray(json?.templates)) setData(json);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { data, loading };
}

export interface TestRunSchemaField {
  id: string;
  label: string;
  type: "text" | "phone" | "select" | "currency" | "date" | string;
  required?: boolean;
  options?: string[];
  example?: string;
  maxLength?: number;
  currency?: string;
  // Real field, confirmed live on most event-detail fields — often an enum
  // spelled out as "value1 | value2 | value3" before a sentence describing
  // it (e.g. kyc_status: "verified | pending | failed."). test-run-modal.tsx
  // parses that pattern to turn a plain-text status-ish field into a real
  // dropdown instead of free text.
  hint?: string;
  // UI-only display override for a `select` field's option text — the
  // submitted value is still the raw `options` entry itself, this just
  // changes what's shown next to it (e.g. new_account_welcome's design shows
  // "Not Updated" where the real kyc_completion_status value is "Not
  // Clicked"). Not part of the live schema; set client-side where needed.
  optionLabels?: Record<string, string>;
  // Only on the schema's "What this journey decides on" section (confirmed
  // live): "you" = the operator has to pick it (a real-time thing a test run
  // can't wait for — a click, a KYC update); "warehouse" = leave it blank and
  // the fetched profile answers it (consent, eligibility, churn, credit, ...).
  source?: "you" | "warehouse" | string;
}

export interface TestRunSchemaSection {
  section: string;
  description?: string;
  fields: TestRunSchemaField[];
}

export interface TestRunSchemaResult {
  journey: string | null;
  event?: string;
  title: string;
  subtitle: string;
  submit_label: string;
  sections: TestRunSchemaSection[];
}

// GET /v1/bot/test-run/schema?journey= — confirmed live, no auth. The actual
// fields for the "Test your new bot journey" modal: "Customer details" on
// every journey, then whatever that journey's own event needs standing in
// for — e.g. "Loan details" (Application ID/Loan type/Application amount)
// for loan_abandonment_recovery, "Verification status" (Day/Status) for
// new_account_welcome, "Card details"+"Transaction details" for
// fraud_anomaly_alert. Replaces the old fixed Customer/Salary/Loan fields
// every journey used to get regardless of what it actually needed.
export function useTestRunSchema(journeyId?: string) {
  const { makeRequest } = useJouneyReq("/v1/bot/test-run/schema", "GET");
  const [data, setData] = useState<TestRunSchemaResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const [json, status] = await makeRequest(undefined, journeyId ? { journey: journeyId } : undefined);
      if (cancelled) return;
      if (status >= 200 && status < 300 && Array.isArray(json?.sections)) setData(json);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyId]);

  return { data, loading };
}

export interface PushTemplateOption {
  id: string;
  name: string;
  title: string;
  body: string;
  variables: string[];
  length: number;
  segments: number;
}

export interface PushTemplatesResult {
  source: "portal" | "designs" | string;
  templates: PushTemplateOption[];
  count: number;
}

// GET /v1/bot/push-templates — confirmed live, no auth. Backs the Send Push
// dropdown and preview — `title` is the notification heading, `body` the text.
export function usePushTemplates(enabled: boolean) {
  const { loading, makeRequest } = useJouneyReq("/v1/bot/push-templates", "GET");
  const [data, setData] = useState<PushTemplatesResult | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      const [json, status] = await makeRequest();
      if (cancelled) return;
      if (status >= 200 && status < 300 && Array.isArray(json?.templates)) setData(json);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { data, loading };
}

export interface AudienceCondition {
  attribute: string;
  operator: string;
  // A single value, or [from, to] for `between` — confirmed live shape:
  // `{"attribute":"credit_score","operator":"between","value":[700,750]}`.
  value: string | number | (string | number)[];
}

export interface AudiencePreviewSample {
  unique_id: string;
  phone?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
}

export interface AudiencePreviewResult {
  matched: number;
  reachable: number;
  unreachable: number;
  truncated: boolean;
  // True for a change operator (crosses_above/crosses_below/moves_into) —
  // `history_note` is only present alongside this, confirmed live: "This
  // condition is about a change, not a state. It is resolved against the
  // previous sweep, so the first sweep after publishing enters nobody."
  needs_history: boolean;
  history_note?: string;
  note?: string;
  sample: AudiencePreviewSample[];
}

export interface AudiencePreviewOutcome {
  result: AudiencePreviewResult | null;
  // Confirmed live 20 Oct: a bad condition (e.g. a text value on a number
  // attribute) returns 400 with `{"detail":{"error":"...","why":"..."}}` —
  // `why` is written to be shown to the operator as-is. Previously this hook
  // dropped the body on any non-2xx, so the panel had nothing to show and
  // fell back to a generic "try again" message that was wrong twice over:
  // nothing was unreachable, and retrying can't fix a bad value.
  error: string | null;
}

// POST /v1/bot/audience/preview — confirmed live, no auth. Cheap enough to
// call on every condition edit (debounced) per the brief; never show
// `matched` alone — an operator told "34 customers" when only 16 are
// reachable believes they're reaching twice as many people as they are.
export function useAudiencePreview() {
  const { loading, makeRequest } = useJouneyReq("/v1/bot/audience/preview", "POST");

  const preview = useCallback(
    async (conditions: AudienceCondition[], match: "all" | "any" = "all", sample = 5): Promise<AudiencePreviewOutcome> => {
      const [json, status] = await makeRequest({ conditions, match, sample });
      if (status >= 200 && status < 300 && typeof json?.matched === "number") return { result: json, error: null };
      const why = typeof json?.detail?.why === "string" ? json.detail.why : typeof json?.detail === "string" ? json.detail : null;
      return { result: null, error: why || "Couldn't check the audience for this condition." };
    },
    [makeRequest]
  );

  return { loading, preview };
}

export type AudienceContactStatus = "will_receive" | "no_contact_details" | "recently_entered" | string;

export interface AudienceContact {
  unique_id: string;
  phone?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  status: AudienceContactStatus;
}

export interface AudienceContactsResult {
  matched: number;
  will_receive: number;
  no_contact_details: number;
  recently_entered: number;
  page: number;
  per_page: number;
  total: number;
  truncated: boolean;
  contacts: AudienceContact[];
  // Confirmed live: present (and the re-entry column stays uncomputed) when
  // `journey_id` wasn't passed — real text: "Pass journey_id to see who the
  // re-entry rule would skip; without it every reachable contact is shown as
  // will_receive."
  re_entry_note?: string;
}

// POST /v1/bot/audience/contacts — confirmed live, no auth. The expanded,
// paginated "who will actually receive this" list behind preview's summary
// numbers — every matched person is a row, including the ones who won't be
// messaged (no_contact_details/recently_entered), rather than silently
// dropped. `journey_id` only makes sense once the journey has a real saved
// id (POST /journeys already happened) — the re-entry rule needs to know
// which journey's own history to check.
export function useAudienceContacts() {
  const { loading, makeRequest } = useJouneyReq("/v1/bot/audience/contacts", "POST");

  const getContacts = useCallback(
    async (conditions: AudienceCondition[], page: number, perPage: number, journeyId?: number, reEntryDays?: number): Promise<AudienceContactsResult | null> => {
      const [json, status] = await makeRequest({
        conditions,
        page,
        per_page: perPage,
        ...(journeyId != null ? { journey_id: journeyId } : {}),
        ...(reEntryDays != null ? { re_entry_days: reEntryDays } : {}),
      });
      if (status >= 200 && status < 300 && Array.isArray(json?.contacts)) return json;
      return null;
    },
    [makeRequest]
  );

  return { loading, getContacts };
}
